// Client for the hydration WSS (Active_Trader_Hydration_WSS). The wire contract
// is docs/protocol.md in that repository: AUTH must be the first frame, INIT
// carries one full day, UPDATE carries only changed minutes, and replay arrives
// as a dense REPLAY_CHUNK sequence between REPLAY_BEGIN and REPLAY_END.

import {
  isValidReplayCoverage,
  isValidReplaySessionMetadata,
  REPLAY_DATE_CACHE_CAPACITY,
} from '../Utils/replayV2';

const DEFAULT_WS_URL = 'ws://localhost:8765/ws';
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 10000;
const BACKOFF_FACTOR = 1.6;
const REPLAY_TIMEOUT_MS = 120000;
const REPLAY_DATES_TIMEOUT_MS = 5000;
// Abandoning a transfer while the socket stays open is the only case that
// needs an explicit cancel. `disconnected` and `stopped` both close the socket,
// and the server frees the slot when it drops the connection; `busy` never
// took the slot in the first place.
const ABANDONED_REPLAY_REASONS = new Set(['timeout', 'superseded']);

// App.js owns the single long-lived connection. ConfigModal's manual resync
// runs from a component that never sees that instance, so the live handle is
// published here rather than threaded through props.
let activeClient = null;

export function getActiveChainSnapshotClient() {
  return activeClient;
}

export function chainSnapshotWssUrl() {
  return (process.env.REACT_APP_CHAIN_SNAPSHOT_WSS_URL);
}

export function hasValidChainSnapshotCredentials(credentials) {
  return Boolean(credentials?.memberId && credentials?.planId);
}

export function validateReplayDatesPayload(payload) {
  const dates = payload?.dates;
  if (
    payload?.schemaVersion !== 2
    || !/^\d{4}-\d{2}-\d{2}$/.test(payload?.latestTradingDate || '')
    || !Array.isArray(dates)
    || dates.length < 1
    || dates.length > REPLAY_DATE_CACHE_CAPACITY
    || new Set(dates.map((entry) => entry?.tradingDate)).size !== dates.length
    || dates.filter((entry) => entry?.isLatest).length !== 1
    || dates.find((entry) => entry?.isLatest)?.tradingDate !== payload.latestTradingDate
    || dates.some((entry) => entry?.session?.open > Date.now())
    || !dates.every((entry) => (
      /^\d{4}-\d{2}-\d{2}$/.test(entry?.tradingDate || '')
      && typeof entry?.isLatest === 'boolean'
      && Number.isFinite(entry?.generatedAt)
      && isValidReplaySessionMetadata(entry?.session)
      && isValidReplayCoverage(entry?.coverage)
    ))
  ) return { ok: false, reason: 'invalid_response' };

  return { ok: true, ...payload };
}

// Reassembles one replay transfer. A gap in `seq` means a chunk was lost, and
// the frames cannot be trusted to be contiguous, so the transfer is discarded
// rather than delivered short.
export function createReplayAssembler(requestId, expected) {
  const chunks = new Map();
  let invalid = false;
  return {
    requestId,
    frameCount: expected?.frameCount ?? 0,
    chunkCount: expected?.chunkCount ?? 0,
    accept(seq, frames) {
      if (!Number.isInteger(seq) || seq < 0 || seq >= this.chunkCount || chunks.has(seq)) {
        invalid = true;
        return;
      }
      chunks.set(seq, frames);
    },
    isDense() {
      if (invalid) return false;
      if (chunks.size !== this.chunkCount) return false;
      for (let seq = 0; seq < this.chunkCount; seq += 1) {
        if (!chunks.has(seq)) return false;
      }
      return true;
    },
    frames() {
      const ordered = [];
      for (let seq = 0; seq < this.chunkCount; seq += 1) {
        ordered.push(...(chunks.get(seq) || []));
      }
      return ordered;
    },
  };
}

export function createReplayV2Assembler(begin) {
  const expected = new Map(Object.entries(begin?.datasets || {}));
  const chunks = new Map();
  const invalidDatasets = new Set();
  let invalid = false;

  for (const [dataset, counts] of expected) {
    if (!Number.isInteger(counts?.recordCount) || counts.recordCount < 0
      || !Number.isInteger(counts?.chunkCount) || counts.chunkCount < 0
      || (counts.chunkCount === 0 && counts.recordCount !== 0)) {
      invalidDatasets.add(dataset);
    }
    chunks.set(dataset, new Map());
  }

  return {
    accept(dataset, seq, chunkCount, records) {
      const counts = expected.get(dataset);
      const datasetChunks = chunks.get(dataset);
      if (!counts || !datasetChunks || chunkCount !== counts.chunkCount
        || !Number.isInteger(seq) || seq < 0 || seq >= counts.chunkCount
        || datasetChunks.has(seq) || !Array.isArray(records)) {
        if (counts) invalidDatasets.add(dataset);
        else invalid = true;
        return;
      }
      datasetChunks.set(seq, records);
    },
    finish(end) {
      if (invalid || end?.schemaVersion !== 2 || !Array.isArray(end?.datasets)
        || (end.tradingDate != null && end.tradingDate !== begin.tradingDate)) return null;
      const completed = new Set(end.datasets);
      if (completed.size !== end.datasets.length
        || [...completed].some((name) => !expected.has(name))) return null;
      const datasets = {};
      for (const dataset of completed) {
        if (invalidDatasets.has(dataset)) continue;
        const counts = expected.get(dataset);
        const datasetChunks = chunks.get(dataset);
        if (datasetChunks.size !== counts.chunkCount) continue;
        const records = [];
        let dense = true;
        for (let seq = 0; seq < counts.chunkCount; seq += 1) {
          if (!datasetChunks.has(seq)) {
            dense = false;
            break;
          }
          records.push(...datasetChunks.get(seq));
        }
        if (!dense || records.length !== counts.recordCount) continue;
        if (end.recordCounts != null && end.recordCounts[dataset] !== records.length) continue;
        datasets[dataset] = records;
      }
      return Object.keys(datasets).length || expected.size === 0 ? datasets : null;
    },
  };
}

function replayCoverageAfterTransfer(begin, datasets) {
  const coverage = { ...(begin?.coverage || {}) };
  for (const dataset of Object.keys(begin?.datasets || {})) {
    if (Object.prototype.hasOwnProperty.call(datasets, dataset) || !coverage[dataset]) continue;
    coverage[dataset] = {
      ...coverage[dataset],
      status: 'unavailable',
      observedKeyCount: 0,
      availableRanges: [],
      missingRanges: [],
      firstRecordAt: null,
      lastRecordAt: null,
      reason: 'INCOMPLETE_TRANSFER',
    };
  }
  return coverage;
}

export function startChainSnapshotClient({
  memberCredentials = null,
  url = null,
  onInit = null,
  onUpdate = null,
  onIndexUpdate = null,
  onStatus = null,
  WebSocketImpl = null,
} = {}) {
  const wsUrl = url || chainSnapshotWssUrl() || DEFAULT_WS_URL;
  const Socket = WebSocketImpl || (typeof WebSocket !== 'undefined' ? WebSocket : null);

  let ws = null;
  let retryTimer = null;
  let backoff = INITIAL_BACKOFF_MS;
  let stopped = false;
  let authenticated = false;
  let pendingReplay = null;
  let replayTimer = null;
  let pendingResync = null;
  let resyncTimer = null;
  let pendingReplayDates = null;
  let replayDatesTimer = null;
  let lastValidReplayDates = null;

  function status(value, detail) {
    try {
      onStatus?.(value, detail);
    } catch (error) {
      console.error('[CHAIN-WSS] status handler failed:', error);
    }
  }

  function settleReplay(result) {
    if (!pendingReplay) return;
    const { resolve, requestId } = pendingReplay;
    pendingReplay = null;
    if (replayTimer) {
      clearTimeout(replayTimer);
      replayTimer = null;
    }
    // The server holds replay_request_id until its transfer task finishes, so
    // abandoning one locally without telling it leaves the next request
    // answered with BUSY. Only abandonment needs this: a transfer the server
    // already finished or failed has released the slot itself.
    if (ABANDONED_REPLAY_REASONS.has(result?.reason)) {
      send({ type: 'REPLAY_CANCEL', data: { requestId } });
    }
    resolve(result);
  }

  function settleResync(result) {
    if (!pendingResync) return;
    const { resolve } = pendingResync;
    pendingResync = null;
    if (resyncTimer) {
      clearTimeout(resyncTimer);
      resyncTimer = null;
    }
    resolve(result);
  }

  function settleReplayDates(result) {
    if (!pendingReplayDates) return;
    const { resolve } = pendingReplayDates;
    pendingReplayDates = null;
    if (replayDatesTimer) {
      clearTimeout(replayDatesTimer);
      replayDatesTimer = null;
    }
    resolve(result);
  }

  function send(message) {
    if (!ws || ws.readyState !== 1) return false;
    try {
      ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('[CHAIN-WSS] send failed:', error);
      return false;
    }
  }

  function onMessage(event) {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    const data = message?.data || {};

    switch (message?.type) {
      case 'AUTH_OK':
        authenticated = true;
        backoff = INITIAL_BACKOFF_MS;
        status('authenticated', data);
        return;

      case 'AUTH_ERROR':
        // Only an upstream verification outage is worth retrying. A refusal is
        // a decision, and reconnecting against it is a hot loop.
        authenticated = false;
        status('unauthorized', data);
        if (data.code !== 'VERIFICATION_UNAVAILABLE') stopped = true;
        try {
          ws?.close();
        } catch {
          // Already closing.
        }
        return;

      case 'INIT': {
        const payload = {
          tradingDate: data.tradingDate,
          snaps: data.snaps || [],
        };
        if (Object.prototype.hasOwnProperty.call(data, 'index')) payload.index = data.index;
        onInit?.(payload);
        settleResync({ ok: true, ...payload });
        return;
      }

      case 'UPDATE':
        onUpdate?.({ tradingDate: data.tradingDate, snaps: data.snaps || [] });
        return;

      case 'INDEX_UPDATE':
        onIndexUpdate?.(data);
        return;

      case 'SNAPSHOT_STATUS':
        status(data.ready ? 'ready' : 'hydrating', data);
        return;

      case 'REPLAY_BEGIN':
        if (pendingReplay?.requestId !== data.requestId) return;
        pendingReplay.schemaVersion = data.schemaVersion || 1;
        pendingReplay.begin = data;
        pendingReplay.assembler = pendingReplay.schemaVersion === 2
          ? createReplayV2Assembler(data)
          : createReplayAssembler(data.requestId, data);
        pendingReplay.tradingDate = data.tradingDate;
        pendingReplay.index = data.index;
        return;

      case 'REPLAY_CHUNK':
        if (pendingReplay?.requestId !== data.requestId) return;
        if (pendingReplay.schemaVersion === 2) {
          pendingReplay.assembler?.accept(data.dataset, data.seq, data.chunkCount, data.records);
        } else {
          pendingReplay.assembler?.accept(data.seq, data.frames || []);
        }
        return;

      case 'REPLAY_END': {
        if (pendingReplay?.requestId !== data.requestId) return;
        const assembler = pendingReplay.assembler;
        if (pendingReplay.schemaVersion === 2) {
          const datasets = assembler?.finish(data);
          if (!datasets) {
            settleReplay({ ok: false, reason: 'incomplete_transfer' });
            return;
          }
          const begin = pendingReplay.begin;
          settleReplay({
            ok: true,
            schemaVersion: 2,
            tradingDate: begin.tradingDate,
            requestedRange: begin.requestedRange,
            generatedAt: begin.generatedAt,
            session: begin.session,
            coverage: replayCoverageAfterTransfer(begin, datasets),
            datasets,
          });
          return;
        }
        if (!assembler?.isDense()) {
          settleReplay({ ok: false, reason: 'incomplete_transfer' });
          return;
        }
        settleReplay({
          ok: true,
          tradingDate: pendingReplay.tradingDate,
          frames: assembler.frames(),
          index: pendingReplay.index,
        });
        return;
      }

      case 'REPLAY_ERROR':
        if (pendingReplay?.requestId !== data.requestId) return;
        // Lowercased so every reason the caller sees shares one convention,
        // whether it came from the server's code vocabulary or from a local
        // failure like `timeout`.
        settleReplay({
          ok: false,
          reason: String(data.code || 'replay_error').toLowerCase(),
        });
        return;

      case 'REPLAY_DATES':
        {
          const result = validateReplayDatesPayload(data);
          if (result.ok) {
            lastValidReplayDates = result;
            settleReplayDates(result);
          } else if (lastValidReplayDates) {
            settleReplayDates({
              ...lastValidReplayDates,
              stale: true,
              validationError: result.reason,
            });
          } else {
            settleReplayDates(result);
          }
        }
        return;

      case 'ERROR':
        return;

      default:
    }
  }

  function open() {
    if (stopped || !Socket) return;
    if (!hasValidChainSnapshotCredentials(memberCredentials)) {
      status('disconnected', { reason: 'no_credentials' });
      return;
    }

    status('connecting');
    try {
      ws = new Socket(wsUrl);
    } catch (error) {
      console.error('[CHAIN-WSS] failed to create WebSocket:', error);
      status('disconnected', { reason: 'connect_failed' });
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      send({
        type: 'AUTH',
        data: {
          memberId: memberCredentials.memberId,
          planId: memberCredentials.planId,
        },
      });
    };

    ws.onmessage = onMessage;

    ws.onerror = (error) => {
      console.error('[CHAIN-WSS] WebSocket error:', error);
    };

    ws.onclose = () => {
      ws = null;
      authenticated = false;
      settleReplay({ ok: false, reason: 'disconnected' });
      settleResync({ ok: false, reason: 'disconnected' });
      settleReplayDates({ ok: false, reason: 'disconnected' });
      status('disconnected');
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (stopped) return;
    const delay = Math.min(MAX_BACKOFF_MS, backoff);
    backoff = Math.floor(backoff * BACKOFF_FACTOR);
    retryTimer = setTimeout(open, delay);
  }

  // One transfer at a time, matching the server's BUSY rule.
  function requestReplay({
    tradingDate = null,
    requestedRange = null,
    sources = ['chain', 'index', 'greeks', 'mi'],
    schemaVersions = null,
    timeoutMs = REPLAY_TIMEOUT_MS,
  } = {}) {
    if (pendingReplay) {
      return Promise.resolve({ ok: false, reason: 'busy' });
    }
    if (!authenticated) {
      return Promise.resolve({ ok: false, reason: 'not_authenticated' });
    }

    const requestId = `r-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    return new Promise((resolve) => {
      pendingReplay = {
        requestId, resolve, assembler: null, schemaVersion: null, begin: null, tradingDate,
      };
      replayTimer = setTimeout(
        () => settleReplay({ ok: false, reason: 'timeout' }),
        timeoutMs
      );
      const data = { requestId };
      if (tradingDate) {
        data.tradingDate = tradingDate;
        data.schemaVersions = schemaVersions || [2, 1];
        data.sources = sources;
        if (requestedRange) data.requestedRange = requestedRange;
      }
      const sent = send({ type: 'REPLAY_REQUEST', data });
      if (!sent) settleReplay({ ok: false, reason: 'disconnected' });
    });
  }

  function cancelReplay(reason = 'superseded') {
    if (!pendingReplay) return false;
    settleReplay({ ok: false, reason });
    return true;
  }

  function requestResync({ timeoutMs = REPLAY_TIMEOUT_MS } = {}) {
    if (pendingResync) {
      return Promise.resolve({ ok: false, reason: 'busy' });
    }
    if (!authenticated) {
      return Promise.resolve({ ok: false, reason: 'not_authenticated' });
    }

    return new Promise((resolve) => {
      pendingResync = { resolve };
      resyncTimer = setTimeout(
        () => settleResync({ ok: false, reason: 'timeout' }),
        timeoutMs
      );
      if (!send({ type: 'RESYNC_REQUEST', data: {} })) {
        settleResync({ ok: false, reason: 'disconnected' });
      }
    });
  }

  function requestReplayDates({ timeoutMs = REPLAY_DATES_TIMEOUT_MS } = {}) {
    if (pendingReplayDates) return Promise.resolve({ ok: false, reason: 'busy' });
    if (!authenticated) return Promise.resolve({ ok: false, reason: 'not_authenticated' });

    return new Promise((resolve) => {
      pendingReplayDates = { resolve };
      replayDatesTimer = setTimeout(
        () => settleReplayDates({ ok: false, reason: 'timeout' }),
        timeoutMs,
      );
      if (!send({ type: 'REPLAY_DATES_REQUEST', data: {} })) {
        settleReplayDates({ ok: false, reason: 'disconnected' });
      }
    });
  }

  open();

  const handle = {
    requestReplay,
    cancelReplay,
    requestResync,
    requestReplayDates,
    isAuthenticated: () => authenticated,
    stop() {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      settleReplay({ ok: false, reason: 'stopped' });
      settleResync({ ok: false, reason: 'stopped' });
      settleReplayDates({ ok: false, reason: 'stopped' });
      if (ws) {
        try {
          ws.close();
        } catch (error) {
          console.error('[CHAIN-WSS] failed to close WebSocket:', error);
        }
      }
      if (activeClient === handle) activeClient = null;
      status('disconnected');
    },
  };

  activeClient = handle;
  return handle;
}
